import React, { useEffect } from 'react';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { Amplify, Auth, API, graphqlOperation } from 'aws-amplify';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import data from '../package.json';
import { setVersion } from './app/appSlice';
import { persistor } from './app/store';
import { Header } from './components';
import { addUser } from './features/AdminPortal/adminSlice';
import { getUser } from './graphql/customQueries';
import { updateUser } from './graphql/mutations';
import Routes from './routes';
import './App.scss';
import './translations';

const App = () => {
  const dispatch = useDispatch();
  const { storedVersion } = useSelector(state => state.app);
  const adminUser = useSelector(state => state.admin.user);
  const { version } = data;
  const { route } = useAuthenticator(context => [context.route]);
  const { signOut } = useAuthenticator(context => [context.signOut]);

  useEffect(() => {
    Amplify.configure({
      API: {
        endpoints: [
          {
            name: 'centerlineportal',
            endpoint: `${process.env.REACT_APP_BASE_URL}`,
            custom_header: async () => ({
              Authorization: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
            }),
          },
        ],
      },
    });
  }, []);

  useIdleTimer({
    timeout: process.env.REACT_APP_TIMEOUT_MINS * 60000,
    onIdle: () => {
      if (route === 'authenticated') {
        signOut();
      }
    },
    debounce: 500,
  });

  useEffect(() => {
    versionCheck();
  }, [storedVersion, version]);

  // Populate admin.user on any page load when authenticated but state was cleared
  // (e.g. after window.location.href redirect from passwordless magic link flow)
  useEffect(() => {
    if (route === 'authenticated' && !adminUser.id) {
      initUser();
    }
  }, [route]);

  const initUser = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const { email } = currentUser.signInUserSession.idToken.payload;
      await API.graphql(graphqlOperation(updateUser, { input: { id: email, lastLogin: new Date().toISOString() } }));
      const response = await API.graphql({ query: getUser, variables: { id: email } });
      const user = response.data.getUser;
      if (user) {
        user.roles =
          currentUser.signInUserSession.idToken.payload['custom:role_admin']?.replace(/\[|\]/g, '').split(', ') ?? [];
        dispatch(addUser(user));
      }
    } catch (e) {
      console.log('initUser error:', e);
    }
  };

  const versionCheck = () => {
    if (semverGreaterThan(version, storedVersion)) {
      refreshCacheAndReload();
    }
  };

  const semverGreaterThan = (versionA, versionB) => {
    const versionsA = versionA.split(/\./g);
    const versionsB = versionB.split(/\./g);
    while (versionsA.length || versionsB.length) {
      const a = Number(versionsA.shift());
      const b = Number(versionsB.shift());
      // eslint-disable-next-line no-continue
      if (a === b) continue;
      // eslint-disable-next-line no-restricted-globals
      return a > b || isNaN(b);
    }
    return false;
  };

  const refreshCacheAndReload = async () => {
    try {
      // Persist the new version and flush it to storage BEFORE reloading.
      // Reloading first raced the redux-persist write: when the reload won,
      // storedVersion never updated and the app reloaded in a loop
      // (the repeated "flashing" seen after every version bump).
      dispatch(setVersion(version));
      await persistor.flush();
      // eslint-disable-next-line no-undef
      if (window?.caches) {
        // eslint-disable-next-line no-undef
        const { caches } = window;
        const cacheNames = await caches.keys();
        const cacheDeletionPromises = cacheNames.map(n => caches.delete(n));
        await Promise.all(cacheDeletionPromises);
        console.log('The cache has been deleted.');
      }
      // eslint-disable-next-line no-undef
      window.location.reload();
    } catch (error) {
      console.log('An error occurred while deleting the cache.', true);
      console.log(error, true);
    }
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_BRAND_TITLE;

    const favicon = document.querySelector("link[rel~='icon']");

    if (favicon) {
      favicon.href = process.env.REACT_APP_BRAND_FAVICON;
    }
  }, []);

  return (
    <div className="root">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Header />
        <div style={{ overflow: 'scroll' }}>
          <Routes />
        </div>
      </BrowserRouter>
    </div>
  );
};
export default App;
